<template>
  <MediaGallery
    title="Documents"
    :items="documents"
    :job-id="jobId"
    media-type="document"
    item-type-label="Document"
    card-class="photo-gallery mt-3"
    :has-max-size-text="true"
    :gallery-options="galleryOptions"
    :get-thumbnail-url="getThumbnailUrl"
    @delete-success="(id) => emit('delete-success', id)"
  >
    <template #header-extra>
      <span class="font-sm mr-2"><b>Max size per document:</b> 20MB</span>
    </template>
    <template #upload-button>
      <SeraButton icon="fa-solid fa-files">
        <label class="mb-0 cursor-pointer">
          Upload Document(s)
          <input
            ref="fileInputRef"
            type="file"
            accept=".jpg, .jpeg, .png, .pdf, .docx, .doc"
            multiple
            class="hidden"
            @change="handleUpload"
          />
        </label>
      </SeraButton>
    </template>
    <template #progress-modal>
      <MediaUploadProgressModal
        v-if="uploading"
        media-type="document"
        :file-count="selectedFiles.length"
        :total-file-size="totalFileSize"
        :progress="progress"
        @hide-modal="handleCancel"
      />
    </template>
    <template
      #viewer-modal="{
        item,
        close,
        canGoPrevious,
        canGoNext,
        goPrevious,
        goNext
      }"
    >
      <DocumentViewerModal
        v-if="item"
        :document="item"
        :can-go-previous="canGoPrevious"
        :can-go-next="canGoNext"
        :go-previous="goPrevious"
        :go-next="goNext"
        @hide-modal="close"
      />
    </template>
  </MediaGallery>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from "vue";
import { EventBus } from "@/lib/event_bus.js";

import SeraButton from "@/components/Button.vue";
import MediaGallery from "@/components/MediaGallery.vue";
import MediaUpload from "@/components/MediaUpload";
import MediaUploadProgressModal from "@/components/MediaUploadProgressModal.vue";
import CONSTANTS from "@/Constants.js";
import { Document } from "@/typings/document";
import { FailedUpload, MediaFile } from "@/typings/media-upload";
import DocumentViewerModal from "./DocumentViewerModal.vue";

const { DOCUMENT_MAX_COUNT, DOCUMENT_MAX_SIZE, MEGABYTE } = CONSTANTS;

interface UploadLink {
  href: string;
}

const props = withDefaults(
  defineProps<{
    documents: Document[];
    jobId: number | null;
    uploadLink: UploadLink;
  }>(),
  {
    documents: () => [],
    jobId: null
  }
);

const emit = defineEmits<{
  "upload-success": [documents: Document[]];
  "delete-success": [documentId: number];
}>();

// Template refs
const fileInputRef = ref<HTMLInputElement | null>(null);

// Upload state
const uploading = ref(false);
const selectedFiles = ref<MediaFile[]>([]);
const progress = ref(0);

let uploader: MediaUpload<Document> | null = null;

// Gallery options for useGalleryActions
const galleryOptions = {
  deleteAction: "deleteJobDocument",
  visibilityAction: "updateJobDocumentVisibility",
  visibilityParamKey: "docId",
  thumbnailEventName: "document-thumbnail-ready"
};

// Computed
const totalFileSize = computed(() => {
  return selectedFiles.value.reduce((total, file) => {
    return total + file.file.size;
  }, 0);
});

function getThumbnailUrl(doc: Document): string {
  return doc.thumbnail?.url || "";
}

// Upload methods
function handleUpload(e: Event) {
  const target = e.target as HTMLInputElement;
  if (!target.files) return;

  if (target.files.length > DOCUMENT_MAX_COUNT) {
    window.notify.error(
      `You can only upload a max of ${DOCUMENT_MAX_COUNT} documents at one time. Try splitting documents into multiple uploads.`
    );
    return;
  }

  const data: MediaFile[] = [];
  for (let i = 0; i < target.files.length; i++) {
    const file = target.files[i];
    data.push({
      file: file,
      fileName: file.name
    });
  }
  uploadDocuments(data);
}

async function uploadDocuments(files: MediaFile[]) {
  uploading.value = true;
  selectedFiles.value = files;
  uploader = new MediaUpload({
    mediaType: "document",
    link: props.uploadLink,
    files
  });
  uploader.onProgress = ({ progress: prog }) => {
    progress.value = prog * 100;
  };
  await uploader.uploadAll();
  handleUploadComplete();
  if (fileInputRef.value) {
    fileInputRef.value.value = "";
  }
}

function formatFileSize(bytes: number): string {
  return `${(bytes / MEGABYTE).toFixed(2)}MB`;
}

function getFailureReason(upload: FailedUpload): string {
  if (upload.reason === "too_large") {
    const maxSizeMB = Math.round(DOCUMENT_MAX_SIZE / MEGABYTE);
    return `exceeds maximum size of ${maxSizeMB}MB`;
  }
  return "failed to upload";
}

function handleUploadComplete() {
  if (!uploader) return;

  // Get uploaded documents before any cleanup
  const uploadedDocuments = uploader.uploadedItems;

  try {
    const { failures, success } = uploader;
    let message = `${success} document(s) uploaded successfully!`;
    if (failures) {
      message = `${failures} out of ${
        success + failures
      } document(s) failed to upload.<br/><br/>`;

      uploader.failedUploads.forEach((upload, i) => {
        const fileSize = formatFileSize(upload.file.size);
        const reason = getFailureReason(upload);
        message += `${i + 1}) ${
          upload.fileName
        } (${fileSize}) - ${reason}<br/>`;
      });

      window.notify.error(message);
    } else {
      window.notify.success(message);
    }
    if (document.hidden) {
      alert(message.replace(/<br\/>/gm, "\n"));
    }
  } catch {
    // no-op
  }

  // Emit uploaded documents before resetting
  emit("upload-success", uploadedDocuments);
  resetUploader();
}

function handleCancel() {
  if (uploader) {
    uploader.onProgress = null;
    uploader.cancel();
    window.notify.error(`Cancelled uploading document(s)`);
  }
  resetUploader();
}

function resetUploader() {
  uploading.value = false;
  selectedFiles.value = [];
  progress.value = 0;
  uploader = null;
  setTimeout(() => {
    const modal = document.querySelector(".modal") as HTMLElement | null;
    if (modal) {
      modal.style.display = "none";
    }
  }, 750);
}

// Lifecycle
onMounted(() => {
  EventBus.$on("job-document-upload-complete", handleUploadComplete);
  window.onbeforeunload = (e: BeforeUnloadEvent) => {
    if (uploading.value) {
      e.preventDefault();
      return "Your pending documents will not be uploaded. Are you sure?";
    }
  };
});

onBeforeUnmount(() => {
  EventBus.$off("job-document-upload-complete", handleUploadComplete);
  window.onbeforeunload = null;
});

// Expose for testing
defineExpose({
  uploading,
  selectedFiles,
  progress,
  totalFileSize
});
</script>
